import { CURRENCY_CODE_SYMBOL_MAP } from '@modules/currency-helpers';
import { DBStripePrice } from 'types/db';
import { useEffect, useState } from 'react';

export function getPriceByCurrencyAndInterval(
  prices: DBStripePrice[],
  currency: string,
  interval: 'month' | 'year'
): DBStripePrice | undefined {
  if (prices.length === 0) return undefined;

  const price = prices.find(
    (price) => price.currency.toLowerCase() === currency.toLowerCase() && price.interval === interval
  );

  if (!price) {
    // If this happens we screwed up our Stripe config
    throw new Error(
      `Expected to find price with currency ${currency} and interval ${interval} but did not exist`
    );
  }

  return price;
}

/**
 * Calculates the effective yearly discount percentage and returns it as a formatted string.
 *
 * @param params - The function parameters.
 * @param params.decimalPlaces - The number of decimal places to display in the discount percentage. (default is 0)
 * @param params.monthlyPriceUnitAmount - The monthly price unit amount, not the effective monthly price.
 * @param params.yearlyPriceUnitAmount - The yearly price unit amount.
 * @returns The effective yearly discount percentage as a string.
 */
export const getEffectiveYearlyDiscountPercentage = ({
  decimalPlaces = 0,
  monthlyPriceUnitAmount,
  yearlyPriceUnitAmount,
}: {
  decimalPlaces: number;
  monthlyPriceUnitAmount: number;
  yearlyPriceUnitAmount: number;
}): string => {
  const monthlyCostAfterAYear = monthlyPriceUnitAmount * 12;
  const discountAmount = monthlyCostAfterAYear - yearlyPriceUnitAmount;
  const discountPercentage = (discountAmount / monthlyCostAfterAYear) * 100;

  const roundedDiscount = discountPercentage.toFixed(decimalPlaces);

  return `${roundedDiscount}%`;
};

type FormatCurrencyParams = {
  locale?: string;
  currencySymbol?: string;
  currencyCode?: string;
};

/**
 * Formats a number as a currency string.
 *
 * @param params - The function parameters.
 * @param params.amount - The number to be formatted.
 * @param params.locale - The locale for currency formatting (default is 'en-US').
 * @param params.currencySymbol - The currency code for formatting (default is '').
 * @param params.currencyCode - The currency code for formatting (default is '').
 * @returns The formatted currency string.
 */
const formatCurrency =
  ({ locale = 'en-US', currencySymbol = '', currencyCode }: FormatCurrencyParams) =>
  (amount: number, displayVariant: 'symbol' | 'full' = 'symbol'): string => {
    const getCurrencySymbol = () => {
      if (currencySymbol) return currencySymbol;
      const symbolFromCode = currencyCode ? CURRENCY_CODE_SYMBOL_MAP[currencyCode] : false;
      if (symbolFromCode) return symbolFromCode;
      return '';
    };

    const currencyCodeDisplay = displayVariant === 'full' && currencyCode ? `${currencyCode} ` : '';

    try {
      return `${currencyCodeDisplay}${getCurrencySymbol() || ''}${new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(amount)}`;
    } catch {
      return `${currencyCodeDisplay}${getCurrencySymbol() || ''}${new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(amount)}`;
    }
  };

/**
 * Calculates and rounds effective monthly price from yearly price.
 *
 * @param params - The function parameters.
 * @param params.yearlyPriceUnitAmount - The yearly cost of the product.
 * @param params.formatter - The currency formatter.
 * @returns The rounded float for effective monthly pricing. Not to be used for billing, or calculations.
 */
export const getEffectiveMonthlyDisplayPriceFromYearlyPrice = (
  yearlyPriceUnitAmount: number,
  formatter: ReturnType<typeof formatCurrency>
): string => {
  const effectiveMonthlyPrice = yearlyPriceUnitAmount / 12;
  return formatter(effectiveMonthlyPrice);
};

export const useFormatCurrency = ({
  currencyCode,
  currencySymbol,
}: Omit<FormatCurrencyParams, 'locale'>): ReturnType<typeof formatCurrency> => {
  const [locale, setLocale] = useState('en-US');

  useEffect(() => {
    if (typeof window !== 'undefined' && navigator.language) {
      setLocale(navigator.language);
    }
  }, []);

  return formatCurrency({ locale, currencySymbol, currencyCode });
};
